import { useQuery, useSuspenseQuery } from "@apollo/client";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ExternalLink, Loader } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Section } from "@roqua/components/sections";
import { Alert, AlertDescription, AlertTitle } from "@roqua/components/ui/alert";
import { Button } from "@roqua/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@roqua/components/ui/select";
import { IQuestionnaire } from "@roqua/quby-frontend";

import { Previewer } from "@/components/Previewer";
import { GetQubyFrontendV2, GetQuestionnaire } from "@/features/Questionnaires/graphql";
import { useSessionContext } from "@/session";

export const Route = createFileRoute(
  "/$organizationKey/$dossierId/questionnaires/show/$questionnaireId",
)({
  component: Page,
});

function Page() {
  const { t } = useTranslation("Questionnaires");
  const { organizationKey, dossierId, legacyRoutes } = useSessionContext();
  const { questionnaireId } = Route.useParams();
  const { data } = useSuspenseQuery(GetQuestionnaire, {
    variables: { organizationKey, questionnaireId },
  });
  const navigate = useNavigate();

  const onChooseMeasurement = (measurementId: string) => {
    navigate({
      to: "/$organizationKey/$dossierId/fill_out_wizard_new/$measurementId/choose",
      params: { organizationKey, dossierId, measurementId: measurementId },
    });
  };

  return (
    <>
      <Section variant="header">
        <div className="flex items-center gap-2">
          <header className="flex-1 text-2xl">{data?.organization.questionnaire?.name}</header>
        </div>
      </Section>

      <Section variant="content">
        <div className="flex items-center justify-end gap-4">
          <Alert variant="warning">
            <AlertTitle>{t("show.warningTitle")}</AlertTitle>
            <AlertDescription>
              <p>{t("show.warningDescription")}</p>
            </AlertDescription>
          </Alert>
          <div className="flex flex-col gap-2">
            <Button asChild className="whitespace-nowrap">
              <a
                rel="noreferrer"
                href={legacyRoutes.questionnairePreviewUrl(questionnaireId)}
                target="_blank"
              >
                <ExternalLink className="me-1 h-4" />
                {t("show.clickToOpenExternally")}
              </a>
            </Button>
            <Select onValueChange={onChooseMeasurement}>
              <SelectTrigger className={"justify-center gap-2"}>
                <SelectValue placeholder={t("show.viewLinkedProtocols")} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {data?.organization.questionnaire?.measurements.length != 0 ? (
                    data?.organization.questionnaire?.measurements.map((measurement) => (
                      <SelectItem key={measurement.id} value={measurement.id}>
                        {`${measurement.protocol.name} → ${measurement.name}`}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="0" disabled>
                      {t("show.noProtocolsFound")}
                    </SelectItem>
                  )}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
        <PreviewQuestionnaire
          questionnaireKey={data?.organization.questionnaire?.key || ""}
          handleSave={() => null}
        />
      </Section>
    </>
  );
}

interface Props {
  questionnaireKey: string;
  handleSave: () => void;
}

// Previews the given questionnaire. GraphQL is smart so we only retrieve the questionnaire
// once at the start even if we change some flags and then preview the questionnaire again.
const PreviewQuestionnaire: React.FunctionComponent<Props> = (props: Props) => {
  const { t } = useTranslation("Questionnaires");
  const results = useQuery(GetQubyFrontendV2, {
    variables: {
      questionnaireKey: props.questionnaireKey,
    },
  });

  if (results.loading) {
    return <Loader />;
  }

  if (!results?.data?.currentOrganization?.questionnaire?.qubyFrontendV2) {
    return <div>{JSON.stringify(results.error)}</div>;
  }

  const qubyFrontendV2Json = results.data.currentOrganization.questionnaire
    .qubyFrontendV2 as IQuestionnaire;

  if (qubyFrontendV2Json.layoutVersion === "v1") {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t("show.previewUnavailableTitle")}</AlertTitle>
        <AlertDescription>
          <p>{t("show.previewUnavailableDescription")} </p>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex border">
      <Previewer questionnaireJson={qubyFrontendV2Json} handleSave={props.handleSave} />
    </div>
  );
};
